import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ProjectList } from "../helpers/ProjectList";
import { ArrowLeft, Github, ExternalLink, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import Unlock from "./Unlock";

const splitList = (value) =>
    (value || "")
        .split(/,| and /)
        .map((item) => item.trim())
        .filter(Boolean);

function ProjectDisplay() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [showUnlockModal, setShowUnlockModal] = useState(false);

    const project = ProjectList.find(p => p.id === parseInt(id, 10));

    if (!project) {
        return (
            <div className="mx-auto max-w-3xl px-6 py-16 text-center">
                <p className="font-mono text-sm text-muted-foreground">404: project not found</p>
                <h1 className="mt-2 text-2xl font-semibold">That project doesn't exist</h1>
                <Button variant="outline" className="mt-6" asChild>
                    <Link to="/projects">
                        <ArrowLeft /> Back to projects
                    </Link>
                </Button>
            </div>
        );
    }

    const handleVisitProject = () => {
        if (project.link) {
            if (project.link.startsWith('/')) {
                navigate(project.link);
            } else {
                window.open(project.link, "_blank", "noopener,noreferrer");
            }
        } else {
            setShowUnlockModal(true);
        }
    };

    const handleVisitGithub = () => {
        if (project.github) {
            window.open(project.github, "_blank", "noopener,noreferrer");
        } else {
            setShowUnlockModal(true);
        }
    };

    return (
        <div className="mx-auto max-w-3xl px-6 py-12">
            <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
                <Link to="/projects">
                    <ArrowLeft /> Back to projects
                </Link>
            </Button>

            <Card className="overflow-hidden">
                <img src={project.image} alt={project.name} className="max-h-80 w-full object-cover" />
                <CardContent className="p-6 sm:p-8">
                    <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{project.name}</h1>
                    <p className="mt-3 text-muted-foreground">{project.description}</p>

                    <div className="mt-6">
                        <p className="font-mono text-xs text-muted-foreground">$ ls tools/</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {splitList(project.tools).map((tool) => (
                                <Badge key={tool} variant="secondary" className="font-mono font-normal">
                                    {tool}
                                </Badge>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Button onClick={handleVisitProject}>
                            {project.link ? <>Visit project <ExternalLink /></> : <>Request access <Lock /></>}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleVisitGithub}
                            aria-label={project.github ? "View on GitHub" : "Request repository access"}
                        >
                            <Github /> {project.github ? "Source" : "Private repo"}
                        </Button>
                    </div>

                    <Separator className="my-6" />

                    <p className="font-mono text-xs text-muted-foreground">$ cat skills.txt</p>
                    <p className="mt-2 text-sm text-muted-foreground">{project.skills}</p>
                </CardContent>
            </Card>

            <Unlock
                showModal={showUnlockModal}
                handleClose={() => setShowUnlockModal(false)}
                projectName={project.name}
            />
        </div>
    );
}

export default ProjectDisplay;
